import React, { useState, FC, CSSProperties, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { dropdownStyle } from '../components/StyledSelect';
import { useAuth } from "../contexts/AuthContext";
import { StreamsProvider, useStreams } from "../contexts/StreamsContext";
import { apiUrl } from "../utils/api";
import DateTimePickerModal from '../components/DateTimePickerModal';

// --- Type Definitions ---

interface Stream {
    id: string;
    name: string;
    status: 'Бурение' | 'Стоп' | 'Не в сети' | 'Ошибка';
    stream_url?: string;
}

interface StreamInstance extends Stream {
    instanceId: number;
}

interface StreamCardProps {
    stream: StreamInstance;
    onRemove: (instanceId: number) => void;
}

interface ArchiveRecord {
    id: string; // recording uuid
    startDate: string;
    endDate: string;
    camera: string;
    company: string;
    location: string;
    drilling: string;
    station: string;
}

// --- API Types ---
interface BroadcastCamera {
    id: string;
    name: string;
    rig_id: string;
    stream_url: string;
    is_online: boolean;
    resolution?: string;
    position_description?: string;
}
interface BroadcastResponse {
    code: number;
    message: string;
    cameras: BroadcastCamera[];
}

// Archive data
const archiveRecords: ArchiveRecord[] = [
    {
        id: "1",
        startDate: "12.03.2025 13:31",
        endDate: "12.03.2025 13:31",
        camera: "Станок 1",
        company: "«Есаульская»",
        location: "Завка на Кш 26-29",
        drilling: "",
        station: ""
    },
    {
        id: "2",
        startDate: "12.03.2025 13:31",
        endDate: "12.03.2025 13:31",
        camera: "Станок 4",
        company: "НШ - 2",
        location: "2 - Ц",
        drilling: "",
        station: ""
    },
    {
        id: "3",
        startDate: "12.03.2025 13:31",
        endDate: "12.03.2025 13:31",
        camera: "Станок 2",
        company: "НШ - 2",
        location: "2 - Ц",
        drilling: "",
        station: ""
    },
    {
        id: "4",
        startDate: "12.03.2025 13:31",
        endDate: "12.03.2025 13:31",
        camera: "Станок 3",
        company: "«Есаульская»",
        location: "2 - Ц",
        drilling: "",
        station: ""
    },
    {
        id: "5",
        startDate: "12.03.2025 13:31",
        endDate: "12.03.2025 13:31",
        camera: "Станок 3",
        company: "«Есаульская»",
        location: "2 - Ц",
        drilling: "",
        station: ""
    }
];

// --- Components ---
// --- WebRTC Player (WHEP) ---
const WebRTCPlayer: FC<{ url: string }> = ({ url }) => {
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const { user } = useAuth();
    const token = user?.access_token;
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let pc: RTCPeerConnection | null = null;
        const abort = new AbortController();
        const cleanupVideoEl = videoRef.current;

        const attachStream = (ev: RTCTrackEvent) => {
            if (!videoRef.current) return;
            const existing = videoRef.current.srcObject as MediaStream | null;
            if (existing) {
                existing.addTrack(ev.track);
            } else {
                videoRef.current.srcObject = new MediaStream([ev.track]);
            }
        };

        const waitIceComplete = (pc: RTCPeerConnection) => new Promise<void>((resolve) => {
            if (pc.iceGatheringState === 'complete') return resolve();
            const onChange = () => {
                if (pc.iceGatheringState === 'complete') {
                    pc.removeEventListener('icegatheringstatechange', onChange);
                    resolve();
                }
            };
            pc.addEventListener('icegatheringstatechange', onChange);
        });

        const start = async () => {
            try {
                setError(null);
                console.log('[WHEP] Starting playback', { url });
                pc = new RTCPeerConnection({ iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] });
                pc.addTransceiver('video', { direction: 'recvonly' });
                pc.addTransceiver('audio', { direction: 'recvonly' });
                pc.addEventListener('track', attachStream);

                const offer = await pc.createOffer();
                console.log('[WHEP] Created local offer');
                await pc.setLocalDescription(offer);
                console.log('[WHEP] Set local description, waiting ICE complete');
                await waitIceComplete(pc);

                const local = pc.localDescription;
                if (!local) throw new Error('No localDescription');

                console.log('[WHEP] Sending offer to gateway', { url, sdpLength: (local.sdp || '').length });
                const res = await fetch(url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/sdp',
                        'Accept': 'application/sdp',
                        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
                    },
                    body: local.sdp || '',
                    signal: abort.signal,
                });
                if (!res.ok) {
                    const text = await res.text().catch(() => '');
                    console.error('[WHEP] Gateway responded error', { status: res.status, bodyPreview: text.slice(0, 256) });
                    throw new Error(`WHEP answer failed: ${res.status}`);
                }
                console.log('[WHEP] Received gateway answer');
                const answerSdp = await res.text();
                console.log('[WHEP] Answer SDP length', answerSdp.length);
                await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });
                console.log('[WHEP] Set remote description, media should attach shortly');
            } catch (err: any) {
                console.error('WebRTC (WHEP) error', err);
                setError(err?.message || 'WebRTC playback failed');
            }
        };

        start();
        return () => {
            abort.abort();
            if (pc) {
                try { pc.getSenders().forEach(s => s.track && s.track.stop()); } catch {}
                try { pc.close(); } catch {}
            }
            const v = cleanupVideoEl;
            if (v && v.srcObject) {
                try { (v.srcObject as MediaStream).getTracks().forEach(t => t.stop()); } catch {}
                v.srcObject = null;
            }
        };
    }, [url, token]);

    return (
        <div style={{ width: '100%', height: '100%', position: 'relative' }}>
            <video
                ref={videoRef}
                autoPlay
                muted
                playsInline
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
            {error && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bbb', background: 'rgba(0,0,0,0.2)' }}>
                    {error}
                </div>
            )}
        </div>
    );
};

const StreamCard: FC<StreamCardProps> = ({ stream, onRemove }) => {
    // Map RTSP to HTTPS WHEP directly: rtsp:// -> https://, port 8554 -> 8889, append /whep
    const buildRtspWhep = (rtsp?: string) => {
        if (!rtsp) return undefined;
        if (!/^rtsp:\/\//i.test(rtsp)) return undefined;
        let https = rtsp.replace(/^rtsp:\/\//i, 'https://');
        // Optionally override host to match TLS certificate via env
        const preferredHost = (process.env.REACT_APP_WHEP_HOST || '').trim();
        try {
            const u = new URL(https);
            const isIPv4 = /^\d{1,3}(?:\.\d{1,3}){3}$/.test(u.hostname);
            if (preferredHost && (isIPv4 || u.hostname !== preferredHost)) {
                u.hostname = preferredHost;
                https = u.toString();
            }
        } catch {}
        // Change explicit 8554 port to 8889
        https = https.replace(/:8554(?!\d)/, ':8889');
        // Append /whep at the end of path (avoid double appending)
        if (/\?/ .test(https)) {
            const [base, qs] = https.split('?');
            const withWhep = /\/whep(?:\/)??$/i.test(base) ? base : base.replace(/\/?$/, '') + '/whep';
            https = qs ? `${withWhep}?${qs}` : withWhep;
        } else {
            https = /\/whep(?:\/)??$/i.test(https) ? https : https.replace(/\/?$/, '') + '/whep';
        }
        console.log('[Streams] RTSP -> WHEP', { rtsp, https });
        return https;
    };
    const isRtsp = /^rtsp:\/\//i.test(stream.stream_url || '');
    const isM3U8 = /\.m3u8($|\?)/i.test(stream.stream_url || '');
    const isHttp = /^https?:\/\//i.test(stream.stream_url || '');
    const rtspWhepUrl = isRtsp ? buildRtspWhep(stream.stream_url) : undefined;
    const showVideo = stream.status !== 'Не в сети' && !!stream.stream_url && (!isRtsp || !!rtspWhepUrl);
    console.log('[Streams] Card decide', { id: stream.id, name: stream.name, status: stream.status, url: stream.stream_url, isRtsp, isM3U8, isHttp, rtspWhepUrl, showVideo });
    const isMp4 = (url?: string) => !!url && /\.mp4($|\?)/i.test(url);
    const videoRef = useRef<HTMLVideoElement | null>(null);

    useEffect(() => {
        let hls: any | undefined;
        let cancelled = false;
        const setup = async () => {
            if (!showVideo || !isM3U8 || !stream.stream_url) return;
            try {
                console.log('[HLS] Loading Hls.js for', stream.stream_url);
                const getHls = () => new Promise<any>((resolve, reject) => {
                    const w = window as any;
                    if (w.Hls) return resolve(w.Hls);
                    const s = document.createElement('script');
                    s.src = 'https://cdn.jsdelivr.net/npm/hls.js@latest';
                    s.async = true;
                    s.onload = () => resolve((window as any).Hls);
                    s.onerror = (err) => reject(err);
                    document.head.appendChild(s);
                });
                const Hls = await getHls();
                if (cancelled || !videoRef.current) return;
                if (Hls && Hls.isSupported()) {
                    console.log('[HLS] Hls.js supported, attaching media');
                    hls = new Hls({ enableWorker: true });
                    hls.loadSource(stream.stream_url);
                    hls.attachMedia(videoRef.current);
                } else if (videoRef.current) {
                    console.log('[HLS] Using native HLS');
                    // Native HLS (Safari)
                    videoRef.current.src = stream.stream_url;
                }
            } catch (e) {
                console.error('HLS load failed', e);
            }
        };
        setup();
        return () => {
            cancelled = true;
            if (hls) {
                try { hls.destroy(); } catch {}
            }
        };
    }, [showVideo, isM3U8, stream.stream_url]);
    return (
        <div style={{
            background: '#1E1E1E',
            borderRadius: '16px',
            overflow: 'hidden',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column'
        }}>
            {showVideo && isM3U8 ? (
                <video
                    ref={videoRef}
                    src={undefined}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    controls={false}
                    autoPlay
                    muted
                    loop
                    playsInline
                >
                    {isMp4(stream.stream_url) && <source src={stream.stream_url} type="video/mp4" />}
                </video>
            ) : showVideo && (isHttp && !isM3U8) ? (
                <WebRTCPlayer url={stream.stream_url!} />
            ) : showVideo && isRtsp && rtspWhepUrl ? (
                <WebRTCPlayer url={rtspWhepUrl} />
            ) : showVideo ? (
                <video
                    src={stream.stream_url}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    controls={false}
                    autoPlay
                    muted
                    loop
                    playsInline
                >
                    {isMp4(stream.stream_url) && <source src={stream.stream_url} type="video/mp4" />}
                </video>
            ) : (
                <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                    <img
                        src={`https://via.placeholder.com/800x600.png/18191c/666666?text=${isRtsp ? 'RTSP%20преобразован%20в%20WHEP' : (stream.status === 'Не в сети' ? 'Нет%20связи' : 'Видеопоток')}`}
                        alt="video stream"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                </div>
            )}
            <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                padding: '12px 16px',
                background: 'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)',
                color: '#fff',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '14px',
                fontWeight: '500'
            }}>
                <span>{stream.name}</span>
                <button
                    onClick={() => onRemove(stream.instanceId)}
                    style={{
                        background: 'rgba(0,0,0,0.3)',
                        border: '1px solid #555',
                        color: '#aaa',
                        cursor: 'pointer',
                        fontSize: '18px',
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        lineHeight: '1',
                        paddingBottom: '2px',
                    }}
                >
                    ×
                </button>
            </div>
        </div>
    );
};

// Archive Modal Component (wired to API)
interface RecordingsParamsResponse {
    code: number;
    message: string;
    deposit_list: Array<{ id: string; id_int?: string; name: string }>;
    mine_list: Array<{ id: string; id_int?: string; name: string; deposit_id: string }>;
    incline_list: Array<{ id: string; id_int?: string; name: string; mine_id: string }>;
    well_list: Array<{ id: string; id_int?: string; name: string; status?: string; current_depth?: string; incline_id: string }>;
    contractor_list: Array<{ id: string; id_int?: string; name: string }>;
    station_list: Array<{ id: string; id_int?: string; name: string; status?: string; well_id?: string; contractor_id?: string }>;
    camera_list: Array<{ id: string; id_int?: string; name: string; stream_url?: string; is_online?: boolean; position_description?: string; well_id?: string }>;
}

interface RecordingsListResponse {
    code: number;
    message: string;
    video_recording_list: Array<{ id: string; camera_id: string; start_time: string; end_time: string; duration_seconds: number; recording_type: string }>;
}

const ArchiveModal: FC<{ isOpen: boolean; onClose: () => void }> = ({ isOpen, onClose }) => {
    const { user } = useAuth();
    const token = user?.access_token;
    const [selectedRecord, setSelectedRecord] = useState<ArchiveRecord | null>(null);
    const [filters, setFilters] = useState<RecordingsParamsResponse | null>(null);
    const [list, setList] = useState<RecordingsListResponse['video_recording_list']>([]);
    const [selected, setSelected] = useState<{ deposit_id?: string; mine_id?: string; incline_id?: string; well_id?: string; contractor_id?: string; station_id?: string; camera_id?: string }>({});
    const [openFrom, setOpenFrom] = useState(false);
    const [openTo, setOpenTo] = useState(false);
    const [dateFrom, setDateFrom] = useState<Date | undefined>(undefined);
    const [dateTo, setDateTo] = useState<Date | undefined>(undefined);
    const navigate = useNavigate();

    const fmtRuDateTime = (d?: Date) => {
        if (!d) return '';
        const pad = (n: number) => String(n).padStart(2,'0');
        const dd = pad(d.getDate());
        const mm = pad(d.getMonth()+1);
        const yy = String(d.getFullYear()).slice(-2);
        const hh = pad(d.getHours());
        const mi = pad(d.getMinutes());
        return `${dd}.${mm}.${yy} ${hh}:${mi}`;
    };

    useEffect(() => {
        if (!isOpen || !token) return;
        const loadParams = async () => {
            try {
                const res = await fetch(apiUrl('/recordings/params'), {
                    method: 'GET',
                    headers: {
                        'Accept': 'application/json',
                        'Authorization': token ? `Bearer ${token}` : '',
                    },
                });
                if (!res.ok) throw new Error(`Params failed: ${res.status}`);
                const data: RecordingsParamsResponse = await res.json();
                setFilters(data);
            } catch (e) {
                console.error(e);
            }
        };
        loadParams();
    }, [isOpen, token]);

    // Default: load all recordings without filters on open
    useEffect(() => {
        if (!isOpen || !token) return;
        startFetchList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, token]);

    // Auto-fetch list when filters or period change
    useEffect(() => {
        if (!isOpen || !token) return;
        const handle = setTimeout(() => {
            startFetchList();
        }, 300);
        return () => clearTimeout(handle);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected, dateFrom, dateTo, isOpen, token]);

    // Avoid conditional hook ordering issues; render hidden when closed

    const filteredMines = (filters?.mine_list || []).filter(m => !selected.deposit_id || m.deposit_id === selected.deposit_id);
    const filteredInclines = (filters?.incline_list || []).filter(i => !selected.mine_id || i.mine_id === selected.mine_id);
    const filteredWells = (filters?.well_list || []).filter(w => !selected.incline_id || w.incline_id === selected.incline_id);
    const filteredStations = (filters?.station_list || []).filter(s => {
        const byWell = !selected.well_id || s.well_id === selected.well_id;
        const byContr = !selected.contractor_id || s.contractor_id === selected.contractor_id;
        return byWell && byContr;
    });
    const filteredCameras = (filters?.camera_list || []).filter(c => !selected.well_id || c.well_id === selected.well_id);

    const startFetchList = async () => {
        try {
            const params = new URLSearchParams();
            const add = (k: keyof typeof selected) => {
                const v = selected[k];
                if (v) params.set(k, v);
            };
            add('deposit_id'); add('mine_id'); add('incline_id'); add('well_id'); add('contractor_id'); add('station_id'); add('camera_id');
            if (dateFrom) params.set('period_from', dateFrom.toISOString());
            if (dateTo) params.set('period_to', dateTo.toISOString());
            const res = await fetch(`${apiUrl('/recordings/list')}?${params.toString()}`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'Authorization': token ? `Bearer ${token}` : '',
                },
            });
            if (!res.ok) throw new Error(`List failed: ${res.status}`);
            const data: RecordingsListResponse = await res.json();
            setList(data.video_recording_list || []);
            setSelectedRecord(null);
        } catch (e) {
            console.error(e);
        }
    };

    const fmt = (s: string) => {
        const d = new Date(s);
        return Number.isFinite(d.getTime()) ? `${d.toLocaleDateString('ru-RU')} ${d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}` : '—';
    };

    const nameById = {
        camera: (id?: string) => (filters?.camera_list || []).find(c => c.id === id)?.name || '—',
        contractor: (id?: string) => (filters?.contractor_list || []).find(c => c.id === id)?.name || '—',
        mineByWell: (wellId?: string) => {
            const incId = (filters?.well_list || []).find(w => w.id === wellId)?.incline_id;
            const mineId = (filters?.incline_list || []).find(i => i.id === incId)?.mine_id;
            return (filters?.mine_list || []).find(m => m.id === mineId)?.name || '—';
        },
        inclineByWell: (wellId?: string) => {
            const incId = (filters?.well_list || []).find(w => w.id === wellId)?.incline_id;
            return (filters?.incline_list || []).find(i => i.id === incId)?.name || '—';
        },
        stationByWellOrContractor: (wellId?: string) => (filters?.station_list || []).find(s => s.well_id === wellId)?.name || '—',
        contractorByWell: (wellId?: string) => {
            const station = (filters?.station_list || []).find(s => s.well_id === wellId);
            return (filters?.contractor_list || []).find(c => c.id === station?.contractor_id)?.name || '—';
        },
    };

    const openRecordingPage = () => {
        if (!selectedRecord?.id) return;
        console.log('[ArchiveModal] Navigate to player', { recordingId: selectedRecord.id });
        navigate(`/streams/recording/${selectedRecord.id}`);
        onClose();
    };

    return (
        <div style={{ ...styles.modalOverlay, display: isOpen ? 'flex' : 'none' }} onClick={onClose}>
            <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
                <div style={styles.modalHeader}>
                    <div style={styles.headerRow}>
                        <select style={styles.modalDropdown} value={selected.contractor_id || ''} onChange={(e) => setSelected(prev => ({ ...prev, contractor_id: e.target.value || undefined }))}>
                            <option value="">Компания</option>
                            {(filters?.contractor_list || []).map(c => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </select>
                        <select style={styles.modalDropdown} value={selected.deposit_id || ''} onChange={(e) => setSelected(prev => ({ ...prev, deposit_id: e.target.value || undefined, mine_id: undefined, incline_id: undefined, well_id: undefined }))}>
                            <option value="">Месторождение</option>
                            {(filters?.deposit_list || []).map(d => (
                                <option key={d.id} value={d.id}>{d.name}</option>
                            ))}
                        </select>
                        <select style={styles.modalDropdown} value={selected.mine_id || ''} onChange={(e) => setSelected(prev => ({ ...prev, mine_id: e.target.value || undefined, incline_id: undefined, well_id: undefined }))}>
                            <option value="">Шахта</option>
                            {filteredMines.map(m => (
                                <option key={m.id} value={m.id}>{m.name}</option>
                            ))}
                        </select>
                        <select style={styles.modalDropdown} value={selected.incline_id || ''} onChange={(e) => setSelected(prev => ({ ...prev, incline_id: e.target.value || undefined, well_id: undefined }))}>
                            <option value="">Месторасположение</option>
                            {filteredInclines.map(i => (
                                <option key={i.id} value={i.id}>{i.name}</option>
                            ))}
                        </select>
                        <select style={styles.modalDropdown} value={selected.well_id || ''} onChange={(e) => setSelected(prev => ({ ...prev, well_id: e.target.value || undefined }))}>
                            <option value="">Скважина</option>
                            {filteredWells.map(w => (
                                <option key={w.id} value={w.id}>{w.name}</option>
                            ))}
                        </select>
                        <select style={styles.modalDropdown} value={selected.station_id || ''} onChange={(e) => setSelected(prev => ({ ...prev, station_id: e.target.value || undefined }))}>
                            <option value="">Станок</option>
                            {filteredStations.map(s => (
                                <option key={s.id} value={s.id}>{s.name}</option>
                            ))}
                        </select>
                        <span style={{ color:'#bbb', fontSize:14, marginLeft: 'auto' }}>с</span>
                        <button
                            onClick={()=>setOpenFrom(true)}
                            style={{ ...styles.headerChip, minWidth: 200, width: 200 }}
                        >
                            <span style={{ color:'#fff' }}>{dateFrom ? fmtRuDateTime(dateFrom) : 'Выбрать дату'}</span>
                            <span style={{ display:'flex', alignItems:'center', justifyContent:'center' }}>
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <rect x="2" y="3" width="12" height="11" rx="2" stroke="#888" strokeWidth="1"/>
                                    <path d="M5 1V4" stroke="#888" strokeWidth="1" strokeLinecap="round"/>
                                    <path d="M11 1V4" stroke="#888" strokeWidth="1" strokeLinecap="round"/>
                                    <path d="M3 6H13" stroke="#888" strokeWidth="1" strokeLinecap="round"/>
                                </svg>
                            </span>
                        </button>
                        <span style={{ color:'#bbb', fontSize:14 }}>по</span>
                        <button
                            onClick={()=>setOpenTo(true)}
                            style={{ ...styles.headerChip, minWidth: 200, width: 200 }}
                        >
                            <span style={{ color:'#fff' }}>{dateTo ? fmtRuDateTime(dateTo) : 'Выбрать дату'}</span>
                            <span style={{ display:'flex', alignItems:'center', justifyContent:'center' }}>
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <rect x="2" y="3" width="12" height="11" rx="2" stroke="#888" strokeWidth="1"/>
                                    <path d="M5 1V4" stroke="#888" strokeWidth="1" strokeLinecap="round"/>
                                    <path d="M11 1V4" stroke="#888" strokeWidth="1" strokeLinecap="round"/>
                                    <path d="M3 6H13" stroke="#888" strokeWidth="1" strokeLinecap="round"/>
                                </svg>
                            </span>
                        </button>
                    </div>
                </div>
                <div style={styles.modalBody}>
                    {/* Left side - Records table */}
                    <div style={styles.recordsSection}>
                        <div style={styles.tableContainer}>
                            <table style={styles.table}>
                                <thead>
                                    <tr style={styles.tableHeader}>
                                        <th style={styles.th}>Начало записи</th>
                                        <th style={styles.th}>Конец записи</th>
                                        <th style={styles.th}>Камера</th>
                                        <th style={styles.th}>Компания</th>
                                        <th style={styles.th}>Месторасположение</th>
                                        <th style={styles.th}>Буровая</th>
                                        <th style={styles.th}>Станок</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {list.map((rec) => {
                                        const wellId = (filters?.camera_list || []).find(c => c.id === rec.camera_id)?.well_id;
                                        return (
                                            <tr 
                                                key={rec.id} 
                                                style={{
                                                    ...styles.tableRow,
                                                    background: selectedRecord?.id === rec.id ? '#3a3b3f' : 'transparent'
                                                }}
                                                onClick={() => setSelectedRecord({
                                                    id: rec.id,
                                                    startDate: fmt(rec.start_time),
                                                    endDate: fmt(rec.end_time),
                                                    camera: nameById.camera(rec.camera_id),
                                                    company: nameById.contractorByWell(wellId),
                                                    location: nameById.inclineByWell(wellId),
                                                    drilling: '',
                                                    station: nameById.stationByWellOrContractor(wellId),
                                                })}
                                            >
                                                <td style={styles.td}>{fmt(rec.start_time)}</td>
                                                <td style={styles.td}>{fmt(rec.end_time)}</td>
                                                <td style={styles.td}>{nameById.camera(rec.camera_id)}</td>
                                                <td style={styles.td}>{nameById.contractorByWell(wellId)}</td>
                                                <td style={styles.td}>{nameById.inclineByWell(wellId)}</td>
                                                <td style={styles.td}>{''}</td>
                                                <td style={styles.td}>{nameById.stationByWellOrContractor(wellId)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    
                    {/* Right side - Video preview */}
                    <div style={styles.videoSection}>
                        <div style={styles.videoTitle}>
                            {selectedRecord ? selectedRecord.camera : 'Выберите запись'}
                        </div>
                        <div style={{ marginTop: 12, display: 'flex', justifyContent: 'center' }}>
                            <button style={styles.startViewButton} onClick={openRecordingPage} disabled={!selectedRecord}>
                                Открыть запись
                            </button>
                        </div>
                        {/* Date pickers */}
                        <DateTimePickerModal
                            isOpen={openFrom}
                            initialDate={dateFrom || new Date()}
                            onClose={()=>setOpenFrom(false)}
                            onSelect={(d)=> setDateFrom(d)}
                            label="С даты"
                        />
                        <DateTimePickerModal
                            isOpen={openTo}
                            initialDate={dateTo || new Date()}
                            onClose={()=>setOpenTo(false)}
                            onSelect={(d)=> setDateTo(d)}
                            label="По дату"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

const StreamsInner: FC = () => {
    const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
    const { availableStreams, selectedStreams, addStream, removeStream } = useStreams();

    
    
    return (
        <div style={{ 
            padding: '16px 8px 8px', // added top padding to avoid header overlap
            display: 'flex', 
            flexDirection: 'column', 
            height: 'calc(100vh - 60px)',
            boxSizing: 'border-box'
        }}>
            <div style={styles.controls}>
                <div style={styles.leftControls}>
                    <select 
                        onChange={(e) => addStream(e.target.value)} 
                        style={styles.dropdown}
                        value=""
                    >
                        <option value="" disabled>Добавить поток...</option>
                        {availableStreams
                            .filter(as => !selectedStreams.some(ss => ss.id === as.id))
                            .map(stream => (
                                <option key={stream.id} value={stream.id}>{stream.name}</option>
                            ))
                        }
                    </select>
                    <div style={styles.tags}>
                        {selectedStreams.map(stream => (
                            <span key={stream.instanceId} style={styles.tag}>
                                {stream.name}
                                <button onClick={() => removeStream(stream.instanceId)} style={styles.tagRemove}>×</button>
                            </span>
                        ))}
                    </div>
                </div>
                
                <button 
                    style={styles.archiveButton}
                    onClick={() => setIsArchiveModalOpen(true)}
                >
                    Посмотреть запись
                </button>
            </div>
            <div style={styles.grid}>
                {selectedStreams.map(stream => (
                    <StreamCard key={stream.instanceId} stream={stream} onRemove={removeStream} />
                ))}
                {selectedStreams.length === 0 && (
                    <div style={styles.placeholder}>
                        Нет выбранных потоков. Добавьте поток из выпадающего списка.
                    </div>
                )}
            </div>
            
            {/* Archive Modal */}
            <ArchiveModal 
                isOpen={isArchiveModalOpen} 
                onClose={() => setIsArchiveModalOpen(false)} 
            />
        </div>
    );
};

export const StreamsPage: FC = () => {
    // Provider keeps state alive even if page unmounts
    return (
        <StreamsProvider>
            <StreamsInner />
        </StreamsProvider>
    );
};

const styles: { [key: string]: CSSProperties } = {
    controls: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        background: '#1F2534',
        padding: '12px 16px',
        borderRadius: 12,
        border: '1px solid #2E3749',
        boxShadow: '0 2px 16px #0000003D',
    },
    leftControls: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
    },
    dropdown: dropdownStyle,
    tags: {
        display: 'flex',
        gap: '8px',
        flexWrap: 'wrap',
    },
    tag: {
        background: '#2C2C2C',
        color: '#fff',
        borderRadius: '12px',
        padding: '4px 8px',
        fontSize: '12px',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
    },
    tagRemove: {
        background: 'none',
        border: 'none',
        color: '#aaa',
        cursor: 'pointer',
        fontSize: '14px',
        lineHeight: '1',
        padding: '0',
    },
    archiveButton: {
        background: "#4472C4",
        color: "#fff",
        border: "none",
        borderRadius: 8,
        padding: "12px 20px",
        cursor: "pointer",
        fontSize: 14,
        fontWeight: 500,
    },
    grid: {
        flex: 1,
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gridTemplateRows: 'repeat(2, 1fr)',
        gap: '16px',
    },
    placeholder: {
        gridColumn: '1 / -1',
        gridRow: '1 / -1',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#888',
        fontSize: '18px',
        background: '#1F2534',
        borderRadius: '16px',
        border: '1px solid #2E3749',
        boxShadow: '0 2px 16px #0000003D',
    },
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
    },
    modalContent: {
        background: '#18191c',
        borderRadius: 12,
        width: '96vw',
        height: '80vh',
        maxWidth: 1600,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
    },
    modalBody: {
        display: 'flex',
        flex: 1,
    },
    recordsSection: {
        flex: 2,
        borderRight: '1px solid #333',
        display: 'flex',
        flexDirection: 'column',
    },
    videoSection: {
        flex: 1,
        padding: 20,
        display: 'flex',
        flexDirection: 'column',
    },
    modalHeader: {
        padding: '16px 20px',
        borderBottom: '1px solid #333',
        background: '#1F2534',
    },
    headerRow: {
        display: 'flex',
        gap: 8,
        rowGap: 8,
        alignItems: 'center',
        color: '#fff',
        fontSize: 12,
        flexWrap: 'nowrap',
    },
    modalDropdown: { ...dropdownStyle, minWidth: 160, flex: '0 0 160px' },
    headerChip: {
        background: '#424F67',
        color: '#fff',
        border: '1px solid #2F3136',
        borderRadius: 4,
        height: 28,
        padding: '4px 12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
    },
    tableContainer: {
        flex: 1,
        overflow: 'auto',
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
    },
    tableHeader: {
        background: '#1F2534',
        borderBottom: '1px solid #333',
    },
    th: {
        padding: '12px 16px',
        textAlign: 'left',
        fontSize: 11,
        color: '#aaa',
        fontWeight: '500',
        textTransform: 'uppercase',
    },
    tableRow: {
        borderBottom: '1px solid #333',
        cursor: 'pointer',
        transition: 'background-color 0.2s',
    },
    td: {
        padding: '12px 16px',
        fontSize: 12,
        color: '#fff',
    },
    videoTitle: {
        fontSize: 16,
        fontWeight: 500,
        color: '#fff',
        marginBottom: 16,
        textAlign: 'center',
    },
    videoContainer: {
        flex: 1,
        borderRadius: 8,
        overflow: 'hidden',
        marginBottom: 20,
        position: 'relative',
    },
    videoPreview: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    },
    playerBar: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        background: '#1F2534',
        border: '1px solid #2E3749',
        boxShadow: '0 2px 16px #0000003D',
        padding: '10px 12px',
        borderRadius: 8,
        marginBottom: 12,
    },
    playButton: {
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        padding: 0,
    },
    timeText: {
        color: '#fff',
        fontSize: 12,
        minWidth: 36,
        textAlign: 'center',
    },
    progressWrapper: {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
    },
    progress: {
        width: '100%',
        appearance: 'none',
        height: 6,
        borderRadius: 3,
        background: '#2D3A54',
        outline: 'none',
    },
    startViewButton: {
        background: "#4472C4",
        color: "#fff",
        border: "none",
        borderRadius: 8,
        padding: "12px 24px",
        cursor: "pointer",
        fontSize: 14,
        fontWeight: 500,
        alignSelf: 'center',
    },
};
